package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.milta.core.localization.LocalStrings
import com.milta.core.localization.SettingsStrings
import com.milta.feature.profile.domain.usecase.GetProfileUseCase
import com.milta.feature.profile.domain.usecase.UpdateLanguageUseCase
import com.milta.feature.settings.presentation.WebSettingsIntent
import com.milta.feature.settings.presentation.WebSettingsNavEvent
import com.milta.feature.settings.presentation.WebSettingsState
import com.milta.feature.settings.presentation.WebSettingsViewModel
import com.milta.feature.subscription.domain.entity.SubscriptionPlan
import com.milta.feature.subscription.domain.usecase.GetMySubscriptionUseCase
import com.milta.feature.subscription.domain.usecase.GetSubscriptionPlansUseCase
import com.milta.feature.team.domain.usecase.DeleteEnterpriseUseCase
import com.milta.feature.team.domain.usecase.GetMyEnterpriseUseCase
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text
import org.koin.compose.koinInject

private data class LanguageOption(val code: String, val nativeName: String, val flagFile: String)

/** MIL-53 — the 10 languages the Dashboard Web language picker must offer, native (endonym) names,
 * never translated by the current interface language (matches [com.milta.feature.profile.presentation.ProfileScreen]'s
 * `languageDisplayName` convention on mobile). Same flag asset files as [WebWelcomeScreen]'s
 * language picker — `flags_flag_de.png` is the one exception generated for this ticket (German is
 * Dashboard-Web-only, see [[decision:mil53-german-tenth-language-fallback]], so no German flag
 * existed among the other 9 languages' assets yet). */
private val SETTINGS_LANGUAGES = listOf(
    LanguageOption("fr", "Français", "flags_flag_fr.png"),
    LanguageOption("nl", "Nederlands", "flags_flag_nl.png"),
    LanguageOption("de", "Deutsch", "flags_flag_de.png"),
    LanguageOption("en", "English", "flags_flag_en.png"),
    LanguageOption("ro", "Română", "flags_flag_ro.png"),
    LanguageOption("bg", "Български", "flags_flag_bg.png"),
    LanguageOption("sq", "Shqip", "flags_flag_sq.png"),
    LanguageOption("pl", "Polski", "flags_flag_pl.png"),
    LanguageOption("it", "Italiano", "flags_flag_it.png"),
    LanguageOption("es", "Español", "flags_flag_es.png"),
)

/**
 * Entry point wired from `Main.kt`'s "Paramètres" nav destination (MIL-53) — Organisation profile
 * (read-only), interface language, subscription (read-only) and the Organisation-deletion critical
 * zone. Two-column layout per the validated mockup: narrower left column (profile + language),
 * wider right column (subscription + critical zone).
 */
@Composable
fun WebSettingsContent(
    onLanguageChanged: (String) -> Unit,
    onOrganizationDeleted: () -> Unit,
) {
    val getProfile: GetProfileUseCase = koinInject()
    val updateLanguage: UpdateLanguageUseCase = koinInject()
    val getMyEnterprise: GetMyEnterpriseUseCase = koinInject()
    val getMySubscription: GetMySubscriptionUseCase = koinInject()
    val getSubscriptionPlans: GetSubscriptionPlansUseCase = koinInject()
    val deleteEnterprise: DeleteEnterpriseUseCase = koinInject()
    val viewModel = remember {
        WebSettingsViewModel(getProfile, updateLanguage, getMyEnterprise, getMySubscription, getSubscriptionPlans, deleteEnterprise)
    }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(viewModel) {
        viewModel.navEvent.collect { event ->
            when (event) {
                WebSettingsNavEvent.OrganizationDeleted -> onOrganizationDeleted()
            }
        }
    }

    // Mirrors ProfileScreen's `LaunchedEffect(state.language) { onLanguageChange(lang) }` — applies
    // both the initial load (Account.Language) and any explicit change to the whole web interface
    // immediately, never the Chauffeur's own mobile-side language (separate account field).
    LaunchedEffect(state.language) {
        val lang = state.language
        if (!lang.isNullOrBlank()) onLanguageChanged(lang)
    }

    val strings = LocalStrings.current.settings
    val commonStrings = LocalStrings.current.common
    val subscriptionStrings = LocalStrings.current.subscription

    Div(
        attrs = {
            style {
                property("display", "grid")
                property("grid-template-columns", "1fr 1.35fr")
                property("gap", "18px")
                property("align-items", "start")
            }
        },
    ) {
        Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "18px") } }) {
            ProfileCard(strings, state)
            LanguageCard(strings, state, onIntent = viewModel::onIntent)
        }
        Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "18px") } }) {
            SubscriptionCard(strings, subscriptionStrings, state)
            DeleteZoneCard(strings, commonStrings, state, onIntent = viewModel::onIntent)
        }
    }

    if (state.showDeleteConfirmDialog) {
        DeleteConfirmDialog(strings, commonStrings, state, onIntent = viewModel::onIntent)
    }
}

@Composable
private fun SettingsCard(title: String, content: @Composable () -> Unit) {
    Div(
        attrs = {
            style {
                property("background", WebSurface)
                property("border", "1px solid $WebBorder")
                property("border-radius", "12px")
                padding(18.px)
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("font-size", "11px"); property("font-weight", "700")
                    property("letter-spacing", "0.06em"); property("text-transform", "uppercase")
                    property("color", WebTextSecondary); property("margin-bottom", "14px")
                }
            },
        ) { Text(title) }
        content()
    }
}

@Composable
private fun ProfileCard(strings: SettingsStrings, state: WebSettingsState) {
    SettingsCard(title = strings.profileSectionTitle) {
        Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-bottom", "6px") } }) {
            Text(strings.profileNameLabel)
        }
        Div(
            attrs = {
                style {
                    property("border", "1px solid $WebBorder"); property("border-radius", "9px")
                    padding(11.px, 14.px); property("font-size", "13.5px"); property("font-weight", "600")
                    property("color", WebTextPrimary); property("background", WebSurfaceDim)
                }
            },
        ) { Text(state.organizationName ?: "…") }
    }
}

@Composable
private fun LanguageCard(strings: SettingsStrings, state: WebSettingsState, onIntent: (WebSettingsIntent) -> Unit) {
    SettingsCard(title = strings.languageSectionTitle) {
        LanguageDropdown(strings = strings, selected = state.language ?: "en", onSelect = { onIntent(WebSettingsIntent.SelectLanguage(it)) })
        val error = state.languageError
        if (error != null) {
            Div(attrs = { style { property("margin-top", "10px") } }) {
                InlineBanner(kind = InlineBannerKind.Error, text = strings.languageErrorGeneric)
            }
        }
    }
}

@Composable
private fun LanguageDropdown(strings: SettingsStrings, selected: String, onSelect: (String) -> Unit) {
    var isOpen by remember { mutableStateOf(false) }
    var query by remember { mutableStateOf("") }
    val selectedOption = SETTINGS_LANGUAGES.firstOrNull { it.code == selected } ?: SETTINGS_LANGUAGES.first()

    Div(attrs = { style { property("position", "relative") } }) {
        Div(
            attrs = {
                onClick { isOpen = !isOpen }
                style {
                    property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between")
                    property("border", "1px solid $WebBorder"); property("border-radius", "9px")
                    padding(11.px, 14.px); property("cursor", "pointer"); property("background", WebSurface)
                }
            },
        ) {
            Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "10px") } }) {
                LanguageFlag(selectedOption.flagFile)
                Div(attrs = { style { property("font-size", "13.5px"); property("font-weight", "600"); property("color", WebTextPrimary) } }) {
                    Text(selectedOption.nativeName)
                }
            }
            Text(if (isOpen) "▲" else "▼")
        }

        if (isOpen) {
            val ordered = SETTINGS_LANGUAGES.sortedByDescending { it.code == selected }
            // All 10 languages shown directly — no longer truncated to 5 with a "+N more" line;
            // the search field still narrows the list down if the user types.
            val visible = if (query.isBlank()) {
                ordered
            } else {
                ordered.filter { it.nativeName.contains(query, ignoreCase = true) || it.code.contains(query, ignoreCase = true) }
            }

            Div(
                attrs = {
                    style {
                        property("position", "absolute")
                        property("top", "calc(100% + 6px)"); property("left", "0"); property("right", "0")
                        property("background", WebSurface); property("border", "1px solid $WebBorder")
                        property("border-radius", "10px"); property("box-shadow", "0 12px 28px rgba(30,58,74,0.18)")
                        property("z-index", "50"); property("overflow", "hidden")
                    }
                },
            ) {
                Div(attrs = { style { padding(10.px, 12.px); property("border-bottom", "1px solid $WebBorder") } }) {
                    Input(
                        type = InputType.Text,
                        attrs = {
                            value(query)
                            attr("placeholder", strings.languageSearchPlaceholder)
                            onInput { event -> query = event.value }
                            style {
                                width(100.percent); property("box-sizing", "border-box")
                                property("border", "1px solid $WebBorder"); property("border-radius", "7px")
                                property("padding", "8px 10px"); property("font-size", "13px")
                            }
                        },
                    )
                }
                Div(attrs = { style { property("max-height", "260px"); property("overflow-y", "auto") } }) {
                    visible.forEach { option ->
                        LanguageRow(option = option, isSelected = option.code == selected) {
                            onSelect(option.code)
                            isOpen = false
                            query = ""
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun LanguageRow(option: LanguageOption, isSelected: Boolean, onClick: () -> Unit) {
    Div(
        attrs = {
            onClick { onClick() }
            style {
                property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between")
                padding(10.px, 14.px); property("cursor", "pointer")
                property("background", if (isSelected) WebActiveTint else WebSurface)
                property("border-bottom", "1px solid $WebSurfaceDim")
            }
        },
    ) {
        Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "10px") } }) {
            LanguageFlag(option.flagFile)
            Div(attrs = { style { property("font-size", "13.5px"); property("color", WebTextPrimary) } }) { Text(option.nativeName) }
        }
        if (isSelected) Text("✓")
    }
}

@Composable
private fun LanguageFlag(flagFile: String) {
    Img(
        src = drawableUri(flagFile),
        alt = "",
        attrs = {
            style {
                width(22.px); height(15.px)
                property("border", "1px solid $WebBorder"); property("border-radius", "2px")
                property("flex-shrink", "0"); property("object-fit", "cover")
            }
        },
    )
}

@Composable
private fun SubscriptionCard(strings: SettingsStrings, subscriptionStrings: com.milta.core.localization.SubscriptionStrings, state: WebSettingsState) {
    SettingsCard(title = strings.subscriptionSectionTitle) {
        val planLabel = when (state.subscriptionPlan) {
            SubscriptionPlan.Organisation -> strings.subscriptionPlanOrganisationLabel
            SubscriptionPlan.Driver -> subscriptionStrings.planDriver
            null -> strings.subscriptionUnavailable
        }
        val price = state.subscriptionPrice
        val currency = state.subscriptionCurrency
        val priceLine = if (price != null && currency != null) {
            "${formatPrice(price, currency)} ${strings.subscriptionPriceSuffix}"
        } else null
        val billingLabel = when (state.subscriptionBillingPeriod) {
            "Monthly" -> strings.subscriptionBillingMonthly
            "Annual" -> strings.subscriptionBillingAnnual
            else -> strings.subscriptionUnavailable
        }
        val renewalLabel = state.subscriptionExpiresAt
            ?.let { formatExpiryDate(it, subscriptionStrings.monthNames) }
            ?: subscriptionStrings.noExpiry

        SubscriptionRow(label = strings.subscriptionPlanLabel, valueLines = listOfNotNull(planLabel, priceLine))
        SubscriptionRow(label = strings.subscriptionBillingLabel, valueLines = listOf(billingLabel))
        SubscriptionRow(label = strings.subscriptionRenewalLabel, valueLines = listOf(renewalLabel), showDivider = false)
    }
}

@Composable
private fun SubscriptionRow(label: String, valueLines: List<String>, showDivider: Boolean = true) {
    Div(
        attrs = {
            style {
                property("display", "flex"); property("align-items", "flex-start"); property("justify-content", "space-between")
                padding(12.px, 0.px)
                if (showDivider) property("border-bottom", "1px solid $WebSurfaceDim")
            }
        },
    ) {
        Div(attrs = { style { property("font-size", "13px"); property("color", WebTextSecondary) } }) { Text(label) }
        Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("align-items", "flex-end"); property("gap", "2px") } }) {
            valueLines.forEach { line ->
                Div(attrs = { style { property("font-size", "13.5px"); property("font-weight", "600"); property("color", WebTextPrimary); property("text-align", "right") } }) {
                    Text(line)
                }
            }
        }
    }
}

@Composable
private fun DeleteZoneCard(
    strings: SettingsStrings,
    commonStrings: com.milta.core.localization.CommonStrings,
    state: WebSettingsState,
    onIntent: (WebSettingsIntent) -> Unit,
) {
    Div(
        attrs = {
            style {
                property("background", WebSurface)
                property("border", "1px solid $WebBorder")
                property("border-left", "4px solid $WebError")
                property("border-radius", "12px")
                padding(22.px)
            }
        },
    ) {
        Div(attrs = { style { property("font-size", "15px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "6px") } }) {
            Text(strings.deleteSectionTitle)
        }
        Div(attrs = { style { property("font-size", "13px"); property("color", WebErrorInk); property("font-weight", "600"); property("margin-bottom", "10px") } }) {
            Text(strings.deleteWarning)
        }
        Div(attrs = { style { property("font-size", "13px"); property("color", WebTextSecondary); property("line-height", "1.5"); property("margin-bottom", "18px") } }) {
            Text(strings.deleteDescription)
        }

        Div(attrs = { style { property("font-size", "12.5px"); property("color", WebTextSecondary); property("margin-bottom", "8px") } }) {
            Text(strings.deleteConfirmLabel)
        }
        Input(
            type = InputType.Text,
            attrs = {
                value(state.deleteConfirmationInput)
                attr("placeholder", state.organizationName.orEmpty())
                onInput { event -> onIntent(WebSettingsIntent.UpdateDeleteConfirmationInput(event.value)) }
                style {
                    width(100.percent); property("box-sizing", "border-box")
                    property("border", "1px solid $WebBorder"); property("border-radius", "9px")
                    property("padding", "11px 14px"); property("font-size", "13.5px"); property("margin-bottom", "16px")
                }
            },
        )

        val nameMatches = state.organizationName != null && state.deleteConfirmationInput == state.organizationName
        Button(
            attrs = {
                if (nameMatches) onClick { onIntent(WebSettingsIntent.OpenDeleteDialog) }
                style {
                    property("border", "1px solid $WebError"); property("border-radius", "8px")
                    property("background", if (nameMatches) WebSurface else WebSurfaceDim)
                    property("color", WebError)
                    property("font-size", "13px"); property("font-weight", "700")
                    property("padding", "10px 16px"); property("cursor", if (nameMatches) "pointer" else "not-allowed")
                    property("opacity", if (nameMatches) "1" else "0.5")
                }
            },
        ) { Text(strings.deleteButton) }
    }
}

@Composable
private fun DeleteConfirmDialog(
    strings: SettingsStrings,
    commonStrings: com.milta.core.localization.CommonStrings,
    state: WebSettingsState,
    onIntent: (WebSettingsIntent) -> Unit,
) {
    Div(
        attrs = {
            style {
                property("position", "fixed"); property("top", "0"); property("left", "0"); property("right", "0"); property("bottom", "0")
                property("background", "rgba(30,58,74,0.45)")
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                property("z-index", "1000")
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("background", WebSurface); property("border-radius", "14px")
                    width(380.px); property("box-sizing", "border-box")
                    property("box-shadow", "0 12px 32px rgba(30,58,74,0.25)")
                    padding(22.px)
                }
            },
        ) {
            Div(attrs = { style { property("font-size", "16px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "10px") } }) {
                Text(strings.deleteFinalDialogTitle)
            }
            Div(attrs = { style { property("font-size", "13.5px"); property("color", WebTextSecondary); property("line-height", "1.5") } }) {
                Text(strings.deleteFinalDialogMessage)
            }

            val error = state.deleteError
            if (error != null) {
                Div(attrs = { style { property("margin-top", "12px") } }) {
                    InlineBanner(kind = InlineBannerKind.Error, text = strings.deleteErrorGeneric)
                }
            }

            Div(attrs = { style { property("display", "flex"); property("justify-content", "flex-end"); property("gap", "10px"); property("margin-top", "20px") } }) {
                Button(
                    attrs = {
                        onClick { onIntent(WebSettingsIntent.DismissDeleteDialog) }
                        style {
                            property("border", "1px solid $WebBorder"); property("border-radius", "8px")
                            property("background", WebSurface); property("color", WebTextPrimary)
                            property("font-size", "13px"); property("font-weight", "600")
                            property("padding", "9px 16px"); property("cursor", "pointer")
                        }
                    },
                ) { Text(commonStrings.cancel) }

                Button(
                    attrs = {
                        if (!state.isDeleting) onClick { onIntent(WebSettingsIntent.ConfirmDelete) }
                        style {
                            property("border", "none"); property("border-radius", "8px")
                            property("background", WebError); property("color", "#FFFFFF")
                            property("font-size", "13px"); property("font-weight", "700")
                            property("padding", "9px 16px"); property("cursor", "pointer")
                            if (state.isDeleting) { property("opacity", "0.6"); property("cursor", "not-allowed") }
                        }
                    },
                ) { Text(strings.deleteButton) }
            }
        }
    }
}

private fun formatPrice(price: Double, currency: String): String {
    val symbol = when (currency) {
        "EUR" -> "€"
        "USD" -> "$"
        else -> currency
    }
    val formatted = price.toString().let { if (it.endsWith(".0")) it.dropLast(2) else it }
    return "$formatted $symbol"
}

/** Formats an ISO-8601 timestamp as "10 juillet 2026" — same helper as
 * [com.milta.feature.subscription.presentation.SubscriptionScreen]'s private `formatExpiryDate`. */
private fun formatExpiryDate(iso: String, monthNames: List<String>): String {
    val datePart = iso.substringBefore('T')
    val parts = datePart.split("-")
    if (parts.size != 3) return iso
    val (year, month, day) = parts
    val monthName = month.toIntOrNull()?.let { monthNames.getOrNull(it - 1) } ?: month
    return "${day.toIntOrNull() ?: day} $monthName $year"
}
